// mobile_v1: responsive — channel cards stack + modal full-screen
import { useEffect, useState } from 'react';
import API from '../api';
import toast from 'react-hot-toast';
import { Plus, Trash2, Copy, Palette, Key, Pencil } from 'lucide-react';
import QuotaPanel from '../components/QuotaPanel'; // quota_v1
import UsageBanner, { useUsageFull } from '../components/UsageBanner'; // usage_banner_ch

const TYPES = ['line', 'telegram', 'facebook', 'whatsapp', 'instagram'];
const FIELDS = {
  line: [['channelAccessToken', 'Channel Access Token'], ['channelSecret', 'Channel Secret']],
  telegram: [['botToken', 'Bot Token']],
  facebook: [['pageAccessToken', 'Page Access Token'], ['verifyToken', 'Verify Token']],
  whatsapp: [['accessToken', 'Access Token'], ['phoneNumberId', 'Phone Number ID'], ['verifyToken', 'Verify Token']],
  instagram: [['pageAccessToken', 'Page Access Token'], ['verifyToken', 'Verify Token']],
};
const PRESET_COLORS = ['#0ea5e9','#22c55e','#f59e0b','#ef4444','#a855f7','#ec4899','#14b8a6','#f97316','#6366f1','#84cc16'];
const TYPE_ICONS = { line: '💚', telegram: '✈️', facebook: '💙', whatsapp: '💬', instagram: '📷' };

export default function Channels() {
  const channelsFull = useUsageFull('channels'); // usage_banner_ch
  const [channels, setChannels] = useState([]);

  // channel_mute_ui_v1: toggle mute state
  const toggleMute = async (channelId, currentMuted) => {
    const newMuted = !currentMuted;
    const action = newMuted ? 'ปิด' : 'เปิด';
    if (!window.confirm(`ยืนยัน${action}การใช้งานช่องนี้?\n\n${newMuted ? '• ช่องนี้จะหายไปจาก sidebar\n• ไม่ส่งการแจ้งเตือน Telegram\n• ข้อความใหม่ยังเข้ามาในระบบปกติ' : '• ช่องนี้จะกลับมาใน sidebar\n• ส่งการแจ้งเตือนตามปกติ'}`)) {
      return;
    }
    try {
      await API.patch(`/api/channels/${channelId}/mute`, { is_muted: newMuted });
      setChannels(prev => prev.map(c => c.id === channelId ? { ...c, is_muted: newMuted } : c));
      if (typeof toast !== 'undefined') {
        toast.success(newMuted ? '🔇 ปิดการใช้งานช่องนี้แล้ว' : '🔔 เปิดการใช้งานช่องนี้แล้ว');
      }
    } catch (err) {
      if (typeof toast !== 'undefined') {
        toast.error('ไม่สำเร็จ: ' + (err.response?.data?.error || err.message));
      } else {
        alert('ไม่สำเร็จ: ' + (err.response?.data?.error || err.message));
      }
    }
  };
  const [show, setShow] = useState(false);
  const [colorPickId, setColorPickId] = useState(null);
  const [form, setForm] = useState({ type: 'line', name: '', credentials: {}, color: '#0ea5e9' });
  /* editCredsUI_v2 */
  const [editCredsCh, setEditCredsCh] = useState(null);
  const [editCredsForm, setEditCredsForm] = useState({});
  /* renameChannel_v1 */
  const [renameCh, setRenameCh] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const openEditCreds = (ch) => {
    const fields = FIELDS[ch.type] || [];
    const initial = {};
    fields.forEach(([key]) => { initial[key] = ''; });
    setEditCredsForm(initial);
    setEditCredsCh(ch);
  };

  const submitEditCreds = async (e) => {
    e.preventDefault();
    try {
      await API.patch(`/api/channels/${editCredsCh.id}/credentials`, { credentials: editCredsForm });
      toast.success('อัพเดท credentials สำเร็จ');
      setEditCredsCh(null);
      setEditCredsForm({});
      load();
    } catch { toast.error('เกิดข้อผิดพลาด'); }
  };

  /* renameChannel_v1 */
  const openRename = (ch) => {
    setRenameValue(ch.name || '');
    setRenameCh(ch);
  };
  const submitRename = async (e) => {
    e.preventDefault();
    const trimmed = renameValue.trim();
    if (!trimmed) { toast.error('ชื่อต้องไม่ว่างเปล่า'); return; }
    if (trimmed.length > 100) { toast.error('ชื่อยาวเกิน 100 ตัวอักษร'); return; }
    if (trimmed === renameCh.name) { setRenameCh(null); return; }
    try {
      await API.patch(`/api/channels/${renameCh.id}/name`, { name: trimmed });
      toast.success('เปลี่ยนชื่อสำเร็จ');
      setRenameCh(null);
      setRenameValue('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'เกิดข้อผิดพลาด');
    }
  };

  const load = () => API.get('/api/channels/all') /* channel_mute_ui_v1 */.then(r => setChannels(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await API.post('/api/channels', form);
      toast.success('เพิ่ม Channel สำเร็จ!');
      navigator.clipboard.writeText(data.webhookUrl);
      toast('📋 คัดลอก Webhook URL แล้ว!', { icon: '📋' });
      setShow(false); load();
    } catch { toast.error('เกิดข้อผิดพลาด'); }
  };

  const updateTag = async (id, tag) => {
    await API.patch(`/api/channels/${id}/color`, { color: channels.find(c=>c.id===id)?.color || '#0ea5e9', tag });
    load();
  };

  const updateColor = async (id, color) => {
    await API.patch(`/api/channels/${id}/color`, { color });
    setColorPickId(null); load();
    toast.success('เปลี่ยนสีแล้ว');
  };

  return (
    <div style={{ padding: isMobile ? '16px 12px' : 0 }}>
      <UsageBanner resource="channels" /> {/* usage_banner_ch */}
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
        gap: isMobile ? 12 : 0,
        marginBottom: isMobile ? 16 : 24,
      }}>
        <h2 style={{ fontSize: isMobile ? 20 : 22, fontWeight: 700, color: '#1e293b', margin: 0 }}>📡 Channels</h2>
        <button onClick={() => { if (channelsFull) { toast.error('ใช้ channel ครบจำนวนที่ plan รองรับแล้ว — อัพเกรดเพื่อเพิ่ม'); return; } setShow(true); }}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            padding: isMobile ? '12px 20px' : '10px 20px',
            background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 8,
            cursor: 'pointer', fontWeight: 600,
            fontSize: isMobile ? 14 : 13,
          }}>
          <Plus size={16} />เพิ่ม Channel
        </button>
      </div>

      <div style={{ display: 'grid', gap: 12 }}>
        {channels.map(ch => (
          <div key={ch.id} style={{
            background: '#fff', borderRadius: 12,
            padding: isMobile ? 14 : 20,
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            borderLeft: `4px solid ${ch.color || '#0ea5e9'}`,
          }}>
            <div style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              alignItems: isMobile ? 'stretch' : 'center',
              gap: isMobile ? 12 : 0,
            }}>
              {/* Left: icon + name */}
              <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 12 : 16, minWidth: 0, flex: 1 }}>
                <div style={{
                  width: isMobile ? 40 : 44,
                  height: isMobile ? 40 : 44,
                  borderRadius: 10,
                  background: (ch.color || '#0ea5e9') + '20',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: isMobile ? 20 : 22,
                  flexShrink: 0,
                }}>
                  {TYPE_ICONS[ch.type] || '💬'}
                </div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: isMobile ? 15 : 15, color: '#1e293b' }}>{ch.name}</span>
                    {ch.tag && <span style={{ background:'#f59e0b20', color:'#92400e', borderRadius:6, padding:'1px 8px', fontSize:11, fontWeight:600 }}>📁 {ch.tag}</span>}
                    {/* channel_mute_ui_v1: muted badge */}
                    {ch.is_muted && <span style={{ background:'#94a3b820', color:'#475569', borderRadius:6, padding:'1px 8px', fontSize:11, fontWeight:600 }}>🔇 ปิดอยู่</span>}
                    {/* channel_mute_ui_v1: toggle button */}
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleMute(ch.id, ch.is_muted); }}
                      style={{
                        marginLeft: 'auto',
                        background: ch.is_muted ? '#fee2e2' : '#dcfce7',
                        color: ch.is_muted ? '#991b1b' : '#166534',
                        border: 'none',
                        borderRadius: 6,
                        padding: '4px 10px',
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: 'pointer',
                      }}
                      title={ch.is_muted ? 'เปิดการใช้งานช่องนี้' : 'ปิดการใช้งานช่องนี้'}
                    >
                      {ch.is_muted ? '🔔 เปิด' : '🔇 ปิด'}
                    </button>
                    <span style={{ background: (ch.color || '#0ea5e9') + '20', color: ch.color || '#0ea5e9', padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{ch.type.toUpperCase()}</span>
                  </div>
                  <p style={{
                    color: '#94a3b8', fontSize: 11, marginTop: 4, marginBottom: 0,
                    wordBreak: 'break-all',
                    overflow: 'hidden',
                    display: '-webkit-box', WebkitLineClamp: isMobile ? 2 : 1, WebkitBoxOrient: 'vertical',
                  }}>{ch.webhook_url}</p>
                </div>
              </div>

              {/* Right: action buttons */}
              <div style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                position: 'relative',
                flexWrap: 'wrap',
                justifyContent: isMobile ? 'flex-start' : 'flex-end',
              }}>
                {/* Color Picker */}
                <button onClick={() => setColorPickId(colorPickId === ch.id ? null : ch.id)}
                  style={{
                    width: isMobile ? 40 : 32,
                    height: isMobile ? 40 : 32,
                    borderRadius: 8,
                    background: ch.color || '#0ea5e9',
                    border: '2px solid #e2e8f0',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                  <Palette size={14} color="#fff" />
                </button>

                {colorPickId === ch.id && (
                  <div style={{
                    position: isMobile ? 'fixed' : 'absolute',
                    right: isMobile ? 12 : 0,
                    left: isMobile ? 12 : 'auto',
                    top: isMobile ? 'auto' : 40,
                    bottom: isMobile ? 80 : 'auto',
                    background: '#fff',
                    borderRadius: 12,
                    padding: 16,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                    zIndex: 100,
                    width: isMobile ? 'auto' : 200,
                  }}>
                    <p style={{ fontSize: 12, color: '#64748b', marginBottom: 10, fontWeight: 600, marginTop: 0 }}>เลือกสี Platform</p>
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? 'repeat(8, 1fr)' : 'repeat(5, 1fr)',
                      gap: 8,
                    }}>
                      {PRESET_COLORS.map(c => (
                        <button key={c} onClick={() => updateColor(ch.id, c)}
                          style={{
                            width: isMobile ? 32 : 28,
                            height: isMobile ? 32 : 28,
                            borderRadius: '50%', background: c,
                            border: ch.color === c ? '3px solid #1e293b' : '2px solid transparent',
                            cursor: 'pointer',
                          }} />
                      ))}
                    </div>
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
                      <p style={{ fontSize: 12, color: '#64748b', marginBottom: 6, fontWeight: 600, marginTop: 0 }}>🏷️ Tag / กลุ่ม</p>
                      <input defaultValue={ch.tag || ''} placeholder="เช่น คาสิโน, ร้านค้า"
                        onBlur={e => updateTag(ch.id, e.target.value || null)}
                        style={{
                          width: '100%',
                          padding: isMobile ? '10px 12px' : '7px 10px',
                          borderRadius: 8, border: '1px solid #e2e8f0',
                          fontSize: isMobile ? 14 : 13, boxSizing: 'border-box',
                          outline: 'none',
                        }} />
                      <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 4, marginBottom: 0 }}>Channel ที่มี Tag เดียวกันจะถูกจัดกลุ่มใน Inbox</p>
                    </div>
                    <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input type="color" defaultValue={ch.color || '#0ea5e9'}
                        onChange={e => updateColor(ch.id, e.target.value)}
                        style={{ width: 32, height: 32, border: 'none', borderRadius: 6, cursor: 'pointer' }} />
                      <span style={{ fontSize: 12, color: '#64748b' }}>กำหนดสีเอง</span>
                    </div>
                    {isMobile && (
                      <button onClick={() => setColorPickId(null)}
                        style={{
                          width: '100%', marginTop: 10,
                          padding: '10px',
                          background: '#f1f5f9', border: 'none', borderRadius: 8,
                          cursor: 'pointer', fontSize: 13,
                        }}>ปิด</button>
                    )}
                  </div>
                )}
                {/* Backdrop on mobile */}
                {isMobile && colorPickId === ch.id && (
                  <div onClick={() => setColorPickId(null)}
                    style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 99 }} />
                )}

                <button onClick={() => openRename(ch)} title="เปลี่ยนชื่อ Channel"
                  style={{
                    padding: isMobile ? '10px' : '8px',
                    background: '#dbeafe', border: 'none', borderRadius: 8,
                    cursor: 'pointer', color: '#2563eb',
                    minWidth: isMobile ? 40 : 'auto', minHeight: isMobile ? 40 : 'auto',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                  <Pencil size={16} />
                </button>
                <button onClick={() => openEditCreds(ch)} title="แก้ไข Credentials"
                  style={{
                    background: '#fef3c7', border: 'none', borderRadius: 8,
                    padding: isMobile ? '8px 12px' : '8px 12px',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 4,
                    minHeight: isMobile ? 40 : 'auto',
                  }}>
                  <Key size={14} color="#d97706" /> <span style={{ fontSize: 12, color: '#92400e' }}>Token</span>
                </button>
                <button onClick={() => { navigator.clipboard.writeText(ch.webhook_url); toast('คัดลอกแล้ว!'); }}
                  style={{
                    padding: isMobile ? '10px' : '8px',
                    background: '#f1f5f9', border: 'none', borderRadius: 8,
                    cursor: 'pointer', color: '#64748b',
                    minWidth: isMobile ? 40 : 'auto', minHeight: isMobile ? 40 : 'auto',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                  <Copy size={16} />
                </button>
                <button onClick={async () => { if (window.confirm('ลบ Channel นี้?')) { await API.delete(`/api/channels/${ch.id}`); load(); } }}
                  style={{
                    padding: isMobile ? '10px' : '8px',
                    background: '#fef2f2', border: 'none', borderRadius: 8,
                    cursor: 'pointer', color: '#ef4444',
                    minWidth: isMobile ? 40 : 'auto', minHeight: isMobile ? 40 : 'auto',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            {/* quota_v1: quota panel for LINE channels */}
            {ch.type === 'line' && <QuotaPanel channel={ch} onUpdate={load} />}
          </div>
        ))}
        {channels.length === 0 && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 48, textAlign: 'center', color: '#94a3b8' }}>
            <p style={{ fontSize: 40, marginBottom: 12 }}>📡</p>
            <p>ยังไม่มี Channel กด "เพิ่ม Channel" เพื่อเริ่มต้น</p>
          </div>
        )}
      </div>

      {/* Add Channel Modal */}
      {show && (
        <div style={{
          position: 'fixed', inset: 0, background: '#00000060',
          display: 'flex',
          alignItems: isMobile ? 'flex-end' : 'center',
          justifyContent: 'center',
          zIndex: 50,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: isMobile ? '16px 16px 0 0' : 16,
            padding: isMobile ? 20 : 32,
            width: isMobile ? '100%' : 480,
            maxWidth: '100%',
            maxHeight: isMobile ? '90vh' : '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}>
            {isMobile && <div style={{ width: 40, height: 4, background: '#e2e8f0', borderRadius: 2, margin: '0 auto 16px' }} />}
            <h3 style={{ marginBottom: 20, marginTop: 0, fontWeight: 700, color: '#1e293b', fontSize: isMobile ? 17 : 18 }}>เพิ่ม Channel ใหม่</h3>
            <form onSubmit={submit}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ color: '#64748b', fontSize: 13, display: 'block', marginBottom: 6 }}>ประเภท</label>
                <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value, credentials: {} })}
                  style={inputStyle(isMobile)}>
                  {TYPES.map(t => <option key={t} value={t}>{TYPE_ICONS[t]} {t.toUpperCase()}</option>)}
                </select>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ color: '#64748b', fontSize: 13, display: 'block', marginBottom: 6 }}>ชื่อ Channel</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="เช่น testchat, MyShop LINE"
                  style={inputStyle(isMobile)} required />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ color: '#64748b', fontSize: 13, display: 'block', marginBottom: 6 }}>สีของ Channel</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {PRESET_COLORS.map(c => (
                    <button type="button" key={c} onClick={() => setForm({ ...form, color: c })}
                      style={{
                        width: isMobile ? 32 : 28,
                        height: isMobile ? 32 : 28,
                        borderRadius: '50%', background: c,
                        border: form.color === c ? '3px solid #1e293b' : '2px solid transparent',
                        cursor: 'pointer',
                      }} />
                  ))}
                  <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })}
                    style={{ width: 32, height: 32, border: 'none', borderRadius: 6, cursor: 'pointer' }} />
                </div>
              </div>
              {(FIELDS[form.type] || []).map(function(item) {
                var key = item[0]; var label = item[1];
                return (
                  <div key={key} style={{ marginBottom: 16 }}>
                    <label style={{ color: '#64748b', fontSize: 13, display: 'block', marginBottom: 6 }}>{label}</label>
                    <input value={form.credentials[key] || ''} onChange={e => {
                      var newCreds = Object.assign({}, form.credentials);
                      newCreds[key] = e.target.value;
                      setForm({ ...form, credentials: newCreds });
                    }} style={inputStyle(isMobile)} required />
                  </div>
                );
              })}
              <div style={{
                display: 'flex',
                gap: 8,
                marginTop: 16,
                flexDirection: isMobile ? 'column' : 'row',
              }}>
                <button type="button" onClick={() => setShow(false)}
                  style={{
                    flex: 1,
                    padding: isMobile ? '12px' : '10px',
                    background: '#f1f5f9', color: '#64748b', border: 'none',
                    borderRadius: 8, cursor: 'pointer',
                    fontSize: 14, fontWeight: 500,
                    order: isMobile ? 2 : 1,
                  }}>ยกเลิก</button>
                <button type="submit"
                  style={{
                    flex: 1,
                    padding: isMobile ? '12px' : '10px',
                    background: form.color, color: '#fff', border: 'none',
                    borderRadius: 8, cursor: 'pointer', fontWeight: 600,
                    fontSize: 14,
                    order: isMobile ? 1 : 2,
                  }}>เพิ่ม Channel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Credentials Modal */}
      {editCredsCh && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex',
          alignItems: isMobile ? 'flex-end' : 'center',
          justifyContent: 'center',
        }} onClick={() => setEditCredsCh(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff',
            borderRadius: isMobile ? '12px 12px 0 0' : 12,
            padding: isMobile ? 20 : 24,
            width: isMobile ? '100%' : '90%',
            maxWidth: 480,
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
          }}>
            {isMobile && <div style={{ width: 40, height: 4, background: '#e2e8f0', borderRadius: 2, margin: '0 auto 16px' }} />}
            <h3 style={{ fontSize: isMobile ? 17 : 18, fontWeight: 700, color: '#1e293b', marginBottom: 4, marginTop: 0 }}>🔑 แก้ไข Credentials</h3>
            <p style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>{editCredsCh.name} ({editCredsCh.type})</p>
            <form onSubmit={submitEditCreds}>
              {(FIELDS[editCredsCh.type] || []).map(function(item) {
                var key = item[0]; var label = item[1];
                return (
                  <div key={key} style={{ marginBottom: 16 }}>
                    <label style={{ color: '#64748b', fontSize: 13, display: 'block', marginBottom: 6 }}>{label}</label>
                    <input type="text" value={editCredsForm[key] || ''} 
                      onChange={e => setEditCredsForm(Object.assign({}, editCredsForm, { [key]: e.target.value }))}
                      placeholder={'ใส่ ' + label + ' ใหม่'}
                      style={{
                        width: '100%',
                        padding: isMobile ? '12px 14px' : '10px 12px',
                        border: '1px solid #e2e8f0', borderRadius: 8,
                        fontSize: isMobile ? 14 : 13,
                        fontFamily: 'monospace',
                        boxSizing: 'border-box', outline: 'none',
                      }} />
                  </div>
                );
              })}
              <div style={{
                background: '#fef3c7',
                padding: isMobile ? 12 : 10,
                borderRadius: 8,
                fontSize: 12, color: '#92400e', marginBottom: 16,
              }}>
                ⚠️ Webhook URL จะคงเดิม ไม่ต้องตั้งค่าใหม่ที่ provider
              </div>
              <div style={{
                display: 'flex',
                gap: 8,
                justifyContent: isMobile ? 'stretch' : 'flex-end',
                flexDirection: isMobile ? 'column' : 'row',
              }}>
                <button type="button" onClick={() => setEditCredsCh(null)}
                  style={{
                    padding: isMobile ? '12px 16px' : '10px 16px',
                    background: '#f1f5f9', border: 'none', borderRadius: 8,
                    cursor: 'pointer', fontSize: 14, fontWeight: 500,
                    order: isMobile ? 2 : 1,
                  }}>ยกเลิก</button>
                <button type="submit"
                  style={{
                    padding: isMobile ? '12px 16px' : '10px 16px',
                    background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 8,
                    cursor: 'pointer', fontSize: 14, fontWeight: 600,
                    order: isMobile ? 1 : 2,
                  }}>บันทึก</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* renameChannel_v1: Modal เปลี่ยนชื่อ Channel */}
      {renameCh && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex',
          alignItems: isMobile ? 'flex-end' : 'center',
          justifyContent: 'center',
        }} onClick={() => setRenameCh(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: '#fff',
            borderRadius: isMobile ? '12px 12px 0 0' : 12,
            padding: isMobile ? 20 : 24,
            width: isMobile ? '100%' : '90%',
            maxWidth: 420,
            boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
          }}>
            {isMobile && <div style={{ width: 40, height: 4, background: '#e2e8f0', borderRadius: 2, margin: '0 auto 16px' }} />}
            <h3 style={{ fontSize: isMobile ? 17 : 18, fontWeight: 700, color: '#1e293b', marginBottom: 4, marginTop: 0 }}>✏️ เปลี่ยนชื่อ Channel</h3>
            <p style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>{renameCh.name} ({renameCh.type})</p>
            <form onSubmit={submitRename}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ color: '#64748b', fontSize: 13, display: 'block', marginBottom: 6 }}>ชื่อใหม่</label>
                <input type="text" value={renameValue}
                  onChange={e => setRenameValue(e.target.value)}
                  placeholder="ใส่ชื่อใหม่"
                  maxLength={100}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: isMobile ? '12px 14px' : '10px 12px',
                    border: '1px solid #e2e8f0', borderRadius: 8,
                    fontSize: isMobile ? 16 : 14,
                    boxSizing: 'border-box', outline: 'none',
                  }} />
              </div>
              <div style={{
                display: 'flex',
                gap: 8,
                justifyContent: isMobile ? 'stretch' : 'flex-end',
                flexDirection: isMobile ? 'column' : 'row',
              }}>
                <button type="button" onClick={() => setRenameCh(null)}
                  style={{
                    padding: isMobile ? '12px 16px' : '10px 16px',
                    background: '#f1f5f9', border: 'none', borderRadius: 8,
                    cursor: 'pointer', fontSize: 14, fontWeight: 500,
                    order: isMobile ? 2 : 1,
                  }}>ยกเลิก</button>
                <button type="submit"
                  style={{
                    padding: isMobile ? '12px 16px' : '10px 16px',
                    background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: 8,
                    cursor: 'pointer', fontSize: 14, fontWeight: 600,
                    order: isMobile ? 1 : 2,
                  }}>บันทึก</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle = (isMobile) => ({
  width: '100%',
  padding: isMobile ? '12px 14px' : '10px 14px',
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  background: '#fff',
  color: '#1e293b',
  fontSize: isMobile ? 16 : 14,
  boxSizing: 'border-box',
  outline: 'none',
});
